'use client'

import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import ExportedImage from 'next-image-export-optimizer'
import { useState } from 'react'
import { CgChevronDown } from 'react-icons/cg'

import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/atoms/ui/carousel'

import { cn } from '@/lib/utils'
import { getAssetData } from '@/lib/utils/get-asset-data'

interface TestimonialProps {
  data: Testimonial
  language?: 'id' | 'en'
}

export const LayananTestimonialCarousel = ({ data, language = 'id' }: TestimonialProps) => {
  const getEntryData = (id?: string) => data?.includes.Entry.find((item) => item.sys.id === id)

  const [showStates, setShowStates] = useState<boolean[]>([false])

  const toggleShow = (index: number) => {
    setShowStates((prevStates) => {
      const newStates = [...prevStates]
      newStates[index] = !newStates[index]

      return newStates
    })
  }

  if (!data.items[0].fields.testimonial.length) return null

  return (
    <Carousel
      id="top"
      opts={{
        align: 'start',
        loop: true
      }}
      className="relative"
    >
      <CarouselContent className="-ml-4 lg:-mr-72">
        {data?.items[0].fields.testimonial.map((item, index) => {
          const testimonial = getEntryData(item.sys.id)?.fields
          const photo = getAssetData(testimonial?.photo?.sys.id, data)?.fields
          const comment = testimonial?.comment ?? ''
          const show = showStates[index]

          return (
            <CarouselItem key={index} className={cn('pl-4 sm:basis-1/2 lg:basis-1/3')}>
              <figure className="h-full px-4 py-5 space-y-4 border border-gray-300 lg:p-8 mx-7 rounded-xl lg:mx-0">
                <div className="flex items-center gap-2 p-2 lg:gap-4 lg:p-3 rounded-2xl bg-blue-50">
                  <ExportedImage
                    src={photo?.file.url ? 'https:' + photo?.file.url : '/static/images/person/user-default.png'}
                    alt={'Picture of ' + photo?.title}
                    width={64}
                    height={64}
                    className="rounded-full size-12 lg:size-16"
                  />

                  <div>
                    <span className="font-bold lg:text-lg text-primary-blue">
                      {testimonial?.position && `${testimonial?.position},`} {testimonial?.institution}
                    </span>
                    <br />
                    <span className="text-sm text-gray-500 lg:text-base">{testimonial?.name}</span>
                  </div>
                </div>

                <figcaption
                  className={cn(
                    'text-sm text-justify lg:text-base',
                    show ? 'line-clamp-none' : 'line-clamp-5 lg:line-clamp-4'
                  )}
                  dangerouslySetInnerHTML={{ __html: comment }}
                />

                {comment.length > 250 && (
                  <button
                    onClick={() => toggleShow(index)}
                    className="flex items-center text-base font-semibold text-secondary-orange"
                  >
                    <span>
                      {show ? (language === 'en' ? 'Show Less' : 'Tutup') : language === 'en' ? 'Show More' : 'Tampilkan'}
                    </span>
                    <CgChevronDown
                      size={24}
                      className={cn('mt-1 transition-all duration-300', show ? 'rotate-180' : 'rotate-0')}
                    />
                  </button>
                )}
              </figure>
            </CarouselItem>
          )
        })}
      </CarouselContent>
      <div>
        <CarouselPrevious
          className="absolute z-10 flex items-center justify-center w-10 h-10 translate-y-[-50%] bg-transparent border border-gray-300 rounded-full text-primary-blue bg-white hover:text-white disabled:text-primary-blue/60 hover:bg-primary-blue top-[50%] -left-2 lg:-left-10 group"
          customArrow={<ChevronLeftIcon className="w-5 h-5" />}
        />
        <CarouselNext
          className="absolute z-10 flex items-center justify-center w-10 h-10 translate-y-[-50%] bg-transparent border border-gray-300 rounded-full text-primary-blue bg-white hover:text-white disabled:text-primary-blue/60 hover:bg-primary-blue top-[50%] -right-2 lg:-right-10 group"
          customArrow={<ChevronRightIcon className="w-5 h-5" />}
        />
      </div>
    </Carousel>
  )
}
